// Loyalty point scheme + earn/redeem helpers, mirroring the Android
// firebase/bean/LoyaltyPoint scheme and CalculateBillFragment's logic:
//   • points earned  = round(billTotal / claimPercentage)
//   • redeem value ₹ = redeemPoints / pointConversion
//   • a redemption is valid only when minPoint ≤ points ≤ maxPoint and the
//     customer actually has that many points.
// `enabled` is driven by the App Setting toggle `enable_loyalty_points`; the
// numeric scheme is configured on the Settings screen and stored here.

import { LoyaltySettings } from '../types';
import { cachedKey } from './idb';

export const DEFAULT_LOYALTY: LoyaltySettings = {
  enabled: false,
  // ₹100 spent earns 1 point.
  claimPercentage: 100,
  // 10 points redeem for ₹1.
  pointConversion: 10,
  minPoint: 10,
  maxPoint: 1000,
};

const loyaltyCache = cachedKey<Partial<LoyaltySettings>>('sconnect.loyalty', {});

export function getLoyaltySettings(): LoyaltySettings {
  return { ...DEFAULT_LOYALTY, ...loyaltyCache.get() };
}

export function saveLoyaltySettings(s: LoyaltySettings): void {
  loyaltyCache.set(s);
}

// Points earned by a bill of the given (final) amount.
export function pointsEarned(amount: number, s: LoyaltySettings): number {
  if (!s.claimPercentage || amount <= 0) return 0;
  return Math.round(amount / s.claimPercentage);
}

// Currency value of redeeming `points`.
export function redeemValue(points: number, s: LoyaltySettings): number {
  if (!s.pointConversion || points <= 0) return 0;
  return points / s.pointConversion;
}

// Why a redemption of `points` is invalid (or null when it's allowed).
export function redeemError(
  points: number,
  available: number,
  s: LoyaltySettings
): string | null {
  if (points <= 0) return null;
  if (points < s.minPoint) return `Minimum ${s.minPoint} points to redeem`;
  if (points > s.maxPoint) return `Maximum ${s.maxPoint} points per bill`;
  if (points > available) return `Only ${available} points available`;
  return null;
}
